<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { createEventDispatcher } from 'svelte';
  import type { SessionInfo, TaskItem, Club } from '../../types';
  import TaskManager from '../TaskManager.svelte';
  import { getGoogleMapsUrl } from '../../lib/utils/shareUtils';
  import PhotoSelectionDialog from '../PhotoSelectionDialog.svelte';
  import ButtonSwitch from '../ButtonSwitch.svelte';
  import SearchableSelect from '../SearchableSelect.svelte';
  import { formatDate } from '../../lib/utils/reportUtils';
  import { checkOnboardingStep } from '../../lib/utils/onboardingUtils';
  import OnboardingModal from '../OnboardingModal.svelte';
  import { trackEvent, copyTasksFromPreviousMatch, getClubs } from '../../lib/apiService';
  import { onMount } from 'svelte';
  import { openDialogWithSlideUp, closeDialogWithSlideDown } from '../../lib/utils/dialogAnimations';

  export let match;
  export let sessionInfo: SessionInfo;
  export let isNewMatch = false; // Flag to indicate if this is a new match creation

  let isEditing = false;
  let editDialogElement: HTMLDialogElement;

  // Export function to allow parent to open dialog
  export async function openEditDialog() {
    isEditing = true;

    // Wait for DOM update and trigger animation
    await new Promise(resolve => setTimeout(resolve, 0));
    if (editDialogElement) {
      await openDialogWithSlideUp(editDialogElement);
    }
  }

  const dispatch = createEventDispatcher();
  
  $: mapsUrl = match.opponent_club ? getGoogleMapsUrl(
    match.opponent_club.location_address,
    match.opponent_club.location_city,
    match.opponent_club.location_zip_code
  ) : '';
  
  // Function to open the date picker
  let dateInput;
  let timeInput;
  let meetingTimeInput;
  
  // Extract date and time from match date
  let dateValue = match.match_date?.split('T')[0] || new Date().toISOString().split('T')[0];
  let timeValue = match.match_date?.split('T')[1]?.substring(0, 5) || '10:00';
  
  // Share button state
  let isSubmitting = false;
  let showOnboardingModal = false;
  let showRequirementsDialog = false;
  let requirementsDialogElement: HTMLDialogElement;

  // Copy tasks state
  let isCopyingTasks = false;

  // Club selection state
  let clubs: Club[] = [];
  let selectedClubId = match.opponent_club?.id || '';
  let clubOptions = [];

  // @ts-ignore - Vite-specific type that TypeScript doesn't know about
  const BUILD_TIMESTAMP = import.meta.env.VITE_BUILD_TIMESTAMP || Date.now();
  
  // Check if user needs account settings onboarding
  async function checkAccountSettingsOnboarding() {
    try {
      const hasSeenOnboarding = await checkOnboardingStep('gameassist_preparation');
      if (!hasSeenOnboarding) {
        // Show welcome modal after a short delay for better UX
        setTimeout(() => {
          showOnboardingModal = true;
        }, 500);
      }
    } catch (error) {
      console.error('Error checking account settings onboarding:', error);
    }
  }

  // Close welcome modal
  function closeOnboardingModal() {
    showOnboardingModal = false;
  }

  // Handle share action for announcement
  async function handleShare() {
    // Check if all requirements are met
    if (!canShare) {
      showRequirementsDialog = true;
      await new Promise(resolve => setTimeout(resolve, 0));
      if (requirementsDialogElement) {
        await openDialogWithSlideUp(requirementsDialogElement);
      }
      return;
    }

    try {
      isSubmitting = true;
      // Notify parent to handle the share action
      dispatch('share', { type: 'announcement' });
    } catch (error) {
      console.error('Error preparing share:', error);
    } finally {
      isSubmitting = false;
    }
  }

  async function closeRequirementsDialog() {
    await closeDialogWithSlideDown(requirementsDialogElement);
    showRequirementsDialog = false;
  }

  // Handle copying tasks from previous match
  async function handleCopyTasksFromPreviousMatch() {
    if (!match.id) {
      console.error('Match ID is required to copy tasks');
      return;
    }

    try {
      isCopyingTasks = true;
      
      copyTasksFromPreviousMatch(match.id, (result, status) => {
        if (status === 200 && result.task_list) {
          // Copy the tasks to the current match
          match.task_list = [...result.task_list];
          
          // Track the event
          trackEvent('copy_tasks_from_previous', {
            module: "match_preparation",
            action_type: "copy_data",
            description: 'Copy tasks from previous match',
            value: match.id,
            previous_match_id: result.previous_match_id,
            tasks_count: result.task_list.length
          });

          // Notify parent component of changes
          notifyChange();
          
          console.log(`Successfully copied ${result.task_list.length} tasks from previous match`);
        } else if (status === 404) {
          console.log('No previous match with tasks found');
          // Could show a toast notification here
        } else {
          console.error('Failed to copy tasks from previous match:', result);
        }
        isCopyingTasks = false;
      });
    } catch (error) {
      console.error('Error copying tasks from previous match:', error);
      isCopyingTasks = false;
    }
  }
  
  function openDatePicker() {
    if (dateInput) {
      dateInput.showPicker();
    }
  }
  
  function openTimePicker(input) {
    if (input) {
      input.showPicker();
    }
  }
  
  // Update match date time when date or time changes
  $: if (dateValue && timeValue) {
    updateMatchDateTime();
  }
   
  function handleLocationChange(event) {
    const { selected_value } = event.detail;
    match.location = selected_value;
    notifyChange();
  }
  
  function updateMatchDateTime() {
    if (!dateValue) return;
    match.match_date = `${dateValue}T${timeValue}:00`;
    trackEvent('update_matchdatetime', {
      module: "match_preparation",
      action_type: "update_data",
      description: 'Update a match date & time',
      value: match.id,
    });
    notifyChange();
  }
  
  // Notify parent component of changes
  function notifyChange() {
    match = { ...match };
    dispatch('change', { match });
  }

  async function handleSaveAndClose() {
    if (isNewMatch) {
      // Validate before dispatching
      if (!match.opponent_club || !match.opponent_club.id) {
        // Don't close dialog, show error in parent
        dispatch('save', { match });
        return;
      }

      if (!match.opponent_team_name || match.opponent_team_name.trim() === '') {
        // Don't close dialog, show error in parent
        dispatch('save', { match });
        return;
      }

      // Valid data - dispatch save event to parent
      // Parent will handle closing and navigation
      dispatch('save', { match });
    } else {
      // For existing matches, just close the dialog
      await closeDialogWithSlideDown(editDialogElement);
      isEditing = false;
    }
  }

  async function handleCancel() {
    if (isNewMatch) {
      // For new matches, dispatch cancel event
      dispatch('cancel');
    } else {
      // For existing matches, just close the dialog
      await closeDialogWithSlideDown(editDialogElement);
      isEditing = false;
    }
  }
  
  // Check if match has no tasks assigned
  $: hasNoTasks = !match.task_list || match.task_list.length === 0;

  // Check if save should be disabled (for new matches)
  $: canSave = !isNewMatch || (
    match.opponent_club &&
    match.opponent_club.id &&
    match.opponent_team_name &&
    match.opponent_team_name.trim() !== ''
  );

  // Todo list checkmarks
  $: hasMatchDetails = match.id !== null; // Match has been created
  $: hasTasks = match.task_list && match.task_list.length > 0;
  $: hasPhoto = match.announcement_photo || match.announcement_photo_filename;

  // Share button should only be enabled if all checkboxes are checked
  $: canShare = hasMatchDetails && hasTasks && hasPhoto;

  // Location options (reactive to language changes)
  $: locationOptions = [
    { value: 'home', label: $_('match.preparation.home') },
    { value: 'away', label: $_('match.preparation.away') }
  ];

  // Available tasks
  const availableTasks: TaskItem[] = [
    { id: 'referee', user_name: '', task_id: '', label: $_('taskmanager.tasks.referee'), icon: '👮‍♂️' },
    { id: 'linesman', user_name: '', task_id: '', label: $_('taskmanager.tasks.linesman'), icon: '🎌' },
    { id: 'keeper', user_name: '', task_id: '', label: $_('taskmanager.tasks.keeper'), icon: '🥅' },
    { id: 'coaching', user_name: '', task_id: '', label: $_('taskmanager.tasks.coaching'), icon: '🧢' },
    { id: 'captain', user_name: '', task_id: '', label: $_('taskmanager.tasks.captain'), icon: '🎽' },
    { id: 'keepersbuddy', user_name: '', task_id: '', label: $_('taskmanager.tasks.keepersbuddy'), icon: '🖐️' },
    { id: 'outfit', user_name: '', task_id: '', label: $_('taskmanager.tasks.outfit'), icon: '👕' },
    { id: 'warming-up', user_name: '', task_id: '', label: $_('taskmanager.tasks.warming-up'), icon: '🏃‍♀️‍➡️' },
    { id: 'materials', user_name: '', task_id: '', label: $_('taskmanager.tasks.materials'), icon: '🧰' },
    { id: 'driving', user_name: '', task_id: '', label: $_('taskmanager.tasks.driving'), icon: '🚗' },
    { id: 'fruit', user_name: '', task_id: '', label: $_('taskmanager.tasks.fruit'), icon: '🍏' },
    { id: 'washing', user_name: '', task_id: '', label: $_('taskmanager.tasks.washing'), icon: '🧼' },
    { id: 'drinks', user_name: '', task_id: '', label: $_('taskmanager.tasks.drinks'), icon: '🥤' },
  ];
  
  // Photo gallery dialog
  let photoDialogOpen = false;
  
  function openPhotoDialog() {
    photoDialogOpen = true;
  }

  function closePhotoDialog() {
    photoDialogOpen = false;
  }

  // Handle club selection
  function handleClubSelect(event) {
    const { value } = event.detail;
    selectedClubId = value;
    const club = clubs.find(c => c.id === value);
    if (club) {
      match.opponent_club = club;
      notifyChange();
    }
  }

  // Handle photo selection
  function handlePhotoSelection(event) {

    const { file, filename, url, type } = event.detail;
    
    match.announcement_photo = file;
    match.announcement_photo_filename = filename;
    match.announcement_photo_url = url;
    match.announcement_photo_type = type;
    notifyChange();
  }

  onMount(async () => {
    await checkAccountSettingsOnboarding();
    
    // Load clubs
    await getClubs((result, status) => {
      if (status === 200) {
        clubs = result;
        // Transform clubs into format needed for SearchableSelect
        clubOptions = clubs.map(club => ({
          value: club.id,
          label: club.name,
          image: club.logo_filename ? `/img/clublogos/${club.logo_filename}?v=${BUILD_TIMESTAMP}` : null
        }));
        
        // If match has an opponent club, set the selected club ID
        if (match.opponent_club?.id) {
          selectedClubId = match.opponent_club.id;
        }
      }
    });
  });

</script>

{#if !isNewMatch}
<article>
    <h5 class="flex items-start">{#if hasMatchDetails}<span class="mr-2 text text-lg text-success"><ion-icon name="checkbox"></ion-icon></span>{:else}<span class="mr-2 text text-lg text-danger"><ion-icon name="square"></ion-icon></span>{/if}{$_('match.preparation.matchDetails')}</h5>
    <div class="">
      <p class="flex items-start mb-2">
        <ion-icon name="flag-outline" class="mr-2 text text-lg flex-shrink-0"></ion-icon>
        <span class="text text-sm">
          {match.opponent_club?.name.substring(0, 14)}
        </span>
        <span class="text text-sm ml-2">
          - {match.location === 'home' ? $_('reportCard.location.home') : $_('reportCard.location.away')}
        </span>
      </p>
      <p class="flex items-start mb-2">
        <ion-icon name="calendar-outline" class="mr-2 text text-lg flex-shrink-0"></ion-icon>
        <span class="text text-sm">{formatDate(match.match_date, true)} ({match.location === 'home' ? $_('match.preparation.home') : $_('match.preparation.away')})</span>
      </p>
      <p class="flex items-start mb-2">
        <ion-icon name="contract-outline" class="mr-2 text text-lg flex-shrink-0"></ion-icon>
        <span class="text text-sm">{$_('match.preparation.meetingLocation')}: {match.meeting_time} {match.meeting_location ? ', ' + match.meeting_location : ''}</span>
      </p>
      <p class="flex items-start mb-2">
        <ion-icon name="navigate-outline" class="mr-2 text text-lg flex-shrink-0"></ion-icon>
        <span class="text text-sm">
          {#if match.opponent_club && match.location.toLowerCase() === 'away' }
              <strong>{match.opponent_club.name}</strong>,  {match.opponent_club.location_name}, {match.opponent_club.location_address}, {match.opponent_club.location_zip_code}, {match.opponent_club.location_city}
              {#if mapsUrl && match.location.toLowerCase() === 'away'}
                , <a href={mapsUrl} target="_blank" rel="noopener noreferrer" class="route-button">
                  {$_('match.preparation.route')}
                </a>
              {/if}
          {:else}
              <strong>{sessionInfo.active_team.club.name}</strong>, {sessionInfo.active_team.club.location_name || ''}, {sessionInfo.active_team.club.location_address || ''}, {sessionInfo.active_team.club.location_zip_code || ''} {sessionInfo.active_team.club.location_city ? "," : ""} {sessionInfo.active_team.club.location_city || ''}
          {/if}
        </span>
      </p>
      <button class="outline full-width mt-2" on:click={openEditDialog}>
        <ion-icon name="pencil-outline"></ion-icon>
        {$_('match.preparation.edit')}
      </button>
    </div>
</article>
{/if}

{#if isEditing}
<dialog bind:this={editDialogElement} class="slide-up-dialog">
  <article class="mt-0">
    <header class="py-4">
      <h5>{$_('match.preparation.edit')}</h5>
      <button class="close-button" on:click={handleCancel} aria-label={$_('common.close')}>×</button>
    </header>

    <h5>{$_('match.preparation.matchDate')}</h5>
    <div class="grid grid-2">
      <div on:click={openDatePicker}>
        <input 
          type="date" 
          bind:value={dateValue}
          bind:this={dateInput}
        />
      </div>
      <div on:click={() => openTimePicker(timeInput)}>
        <input 
          type="time" 
          bind:value={timeValue}
          bind:this={timeInput}
        />
      </div>
    </div>


    <!-- Opponent Club and Team -->
    <h5>{$_('new_match.opponent')}</h5>
    <div class="grid grid-2">
      <div>
        <SearchableSelect 
          options={clubOptions}
          bind:value={selectedClubId}
          placeholder={$_('new_match.selectClub')}
          on:change={handleClubSelect}
        />
      </div>
      <div>
        <input 
          type="text" 
          bind:value={match.opponent_team_name} 
          placeholder={$_('new_match.opponentTeamPlaceholder')}
          on:change={notifyChange}
        />
      </div>
    </div>

    <!-- Match Location -->
    <h5>{$_('new_match.location')}</h5>
    <ButtonSwitch selected_value={match.location} options={locationOptions} on:change={handleLocationChange} />
    <h5>{$_('match.preparation.meetingLocation')}</h5>
    
    <div class="grid grid-2">
    <div on:click={() => openTimePicker(meetingTimeInput)}>
      <input
        type="time"
        bind:value={match.meeting_time}
        bind:this={meetingTimeInput}
        on:change={notifyChange}
      />
    </div>
    <input
      type="text"
      bind:value={match.meeting_location}
      placeholder={$_('match.preparation.meetingLocationPlaceholder')}
      on:change={notifyChange}
    />
    </div>

    <footer class="mt-4">
      {#if isNewMatch}
        <div class="grid grid-2">
          <button class="outline" on:click={handleCancel}>
            {$_('common.cancel')}
          </button>
          <button class="primary" on:click={handleSaveAndClose} disabled={!canSave}>
            {$_('common.save')}
          </button>
        </div>
      {:else}
        <button class="primary full-width" on:click={handleCancel}>
          {$_('common.close')}
        </button>
      {/if}
    </footer>
  </article>
</dialog>
{/if}

<article>
  <h5 class="mb-6 flex items-start">{#if hasTasks}<span class="mr-2 text text-lg text-success"><ion-icon name="checkbox"></ion-icon></span>{:else}<span class="mr-2 text text-lg text-danger"><ion-icon name="square"></ion-icon></span>{/if}{$_('match.preparation.tasks')}</h5>
  
  <div class="">
  {#if hasNoTasks}
    <button 
      class="outline full-width mb-2" 
      on:click={handleCopyTasksFromPreviousMatch}
      disabled={isCopyingTasks}
    >
      <ion-icon name="copy-outline"></ion-icon>
      {$_('match.preparation.copyTasksFromLastMatch')}
    </button>
  {/if}
  
  <TaskManager 
      availableTasks={availableTasks}
      bind:taskList={match.task_list} 
      on:update={(event) => {
        setTimeout(() => {
          notifyChange();
        }, 0);
      }}
    />
    </div>
</article>
<article>
  <h5 class="mb-6 flex items-start">{#if hasPhoto}<span class="mr-2 text text-lg text-success"><ion-icon name="checkbox"></ion-icon></span>{:else}<span class="mr-2 text text-lg text-danger"><ion-icon name="square"></ion-icon></span>{/if}{$_('match.preparation.photo')}</h5>
  <div class="">

    <button class="outline full-width" on:click={openPhotoDialog}>
      <ion-icon name="image"></ion-icon>
        {$_('match.preparation.chooseImage')}
    </button>
    <div class="photo-preview-container">
      {#if match.announcement_photo}
        <div class="photo-preview">
          <img src={URL.createObjectURL(match.announcement_photo)} alt="Preview" />
        </div>
        <div class="file-name">{match.announcement_photo.name}</div>
      {:else if match.announcement_photo_filename}
        <div class="photo-preview">
            {#if match.announcement_photo_url}
              <img src={match.announcement_photo_url} alt="Preview" />
            {:else if match.announcement_photo_filename.includes('/')}
              <!-- User uploaded photo with user_id/filename format -->
              <img src={`/uploads/${match.announcement_photo_filename}`} alt="Preview" />
            {:else}
              <!-- Stock photo -->
              <img src={`/img/backgrounds/match/${sessionInfo.active_team.club.sport}/${match.announcement_photo_filename}`} alt="Preview" />
            {/if}
          </div>
          <div class="text text-sm">{match.announcement_photo_filename.includes('/') ? match.announcement_photo_filename.split('/').pop() : match.announcement_photo_filename}</div>
      {:else}
        <div class="file-name">{$_('match.preparation.noPhoto')}</div>
      {/if}
    </div>

    <!-- Photo selection dialog -->
    {#if photoDialogOpen}
        <PhotoSelectionDialog 
          sport={sessionInfo.active_team.club.sport} 
          photoType="announcement"
          matchId={match.id} 
          on:close={closePhotoDialog}
          on:select={handlePhotoSelection}
        />
    {/if}
    </div>
  </article>

{#if !isNewMatch}
<!-- Share button container -->
<div class="sticky-footer">
  <div class="sticky-footer-buttons">
    <button
      class="sticky-footer-btn"
      on:click={handleShare}
      disabled={isSubmitting}
    >
      <ion-icon name="images-outline"></ion-icon>
      {$_('match.navigation.create_announcement')}
    </button>
  </div>
</div>
{/if}

<!-- Requirements Dialog -->
{#if showRequirementsDialog}
<dialog bind:this={requirementsDialogElement} class="slide-up-dialog">
  <article class="mt-0">
    <header class="py-4">
      <h5>{$_('match.preparation.requirementsTitle')}</h5>
    </header>

    <p class="mb-4">{$_('match.preparation.requirementsMessage')}</p>

    <div class="requirements-list">
      {#if !hasMatchDetails}
      <div class="requirement-item" class:completed={hasMatchDetails}>
        <span class="mr-2 text text-lg" class:text-danger={!hasMatchDetails}>
          <ion-icon name={hasMatchDetails ? "checkbox" : "square"}></ion-icon>
        </span>
        <span>{$_('match.preparation.matchDetails')}</span>
      </div>
      {/if}
      {#if !hasTasks}
      <div class="requirement-item" class:completed={hasTasks}>
        <span class="mr-2 text text-lg" class:text-danger={!hasTasks}>
          <ion-icon name={hasTasks ? "checkbox" : "square"}></ion-icon>
        </span>
        <span>{$_('match.preparation.tasks')}</span>
      </div>
      {/if}
      {#if !hasPhoto}
      <div class="requirement-item" class:completed={hasPhoto}>
        <span class="mr-2 text text-lg" class:text-danger={!hasPhoto}>
          <ion-icon name={hasPhoto ? "checkbox" : "square"}></ion-icon>
        </span>
        <span>{$_('match.preparation.photo')}</span>
      </div>
      {/if}
    </div>

    <footer class="mt-4">
      <button class="primary full-width" on:click={closeRequirementsDialog}>
        {$_('common.close')}
      </button>
    </footer>
  </article>
</dialog>
{/if}


<!-- Onboarding Modal -->
<OnboardingModal
  isOpen={showOnboardingModal}
  title={$_('onboarding.gameassist_preparation.title')}
  onClose={closeOnboardingModal}
>
  <p>{$_('onboarding.gameassist_preparation.info')}</p>
  <ul class="list-disc list-inside">
    <li>{$_('onboarding.gameassist_preparation.step1')}</li>
    <li>{$_('onboarding.gameassist_preparation.step2')}</li>
    <li>{$_('onboarding.gameassist_preparation.step3')}</li>
  </ul>
</OnboardingModal>

<style>
  .photo-preview-container {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-top: 0.5rem;
  }

  .photo-preview {
    width: 60px;
    height: 40px;
    border-radius: var(--pico-border-radius);
    overflow: hidden;
    flex-shrink: 0;
  }

  .photo-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .file-name {
    font-size: 0.875rem;
    color: var(--pico-color-secondary-text);
    flex: 1;
  }

  input[type="time"], input[type="date"] {
    width: 100%;
    cursor: pointer;
  }

  .requirements-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .requirement-item {
    display: flex;
    align-items: center;
    padding: 0.5rem;
    border-radius: var(--pico-border-radius);
    background-color: var(--pico-form-element-background-color);
  }

  .requirement-item.completed {
    opacity: 0.7;
  }
</style> 